import {
  createBenefitMutation,
  deleteBenefitMutation,
} from '@/api/benefits/benefitMutations';
import { getBenefitsQuery } from '@/api/benefits/benefitQueries';
import { Button } from '@/components/common/Button';
import { ConfirmDeleteDialog } from '@/components/common/ConfirmDeleteDialog';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import { TableCell, TableHead } from '@/components/common/Table';
import type { Benefit } from '@/domain/Benefit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { DashboardTableAccordion } from '../../DashboardTableAccordion';
import { BenefitDialog } from './BenefitDialog';

export const Benefits = () => {
  const [page, setPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [benefitIdToDelete, setBenefitIdToDelete] = useState<number | null>(
    null,
  );

  const queryClient = useQueryClient();
  const { data: response, isLoading } = useQuery(getBenefitsQuery(page));

  const benefits = response?.data || [];
  const meta = response?.meta;

  const benefitsQueryKey = ['adminIndicatorBenefits'];

  const { mutate: createBenefit, isPending: isCreating } = useMutation({
    ...createBenefitMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: benefitsQueryKey });
      toast.success('Benefit created successfully');
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create benefit');
    },
  });

  const { mutate: deleteBenefit, isPending: isDeleting } = useMutation({
    ...deleteBenefitMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: benefitsQueryKey });
      toast.success('Benefit deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete benefit');
    },
  });

  const handleAddNew = () => {
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setBenefitIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (benefitIdToDelete !== null) {
      deleteBenefit(benefitIdToDelete);
      setDeleteDialogOpen(false);
      setBenefitIdToDelete(null);
    }
  };

  const handleSubmit = (data: { text: string }) => {
    createBenefit(data);
  };

  return (
    <>
      <DashboardTableAccordion
        value="Benefits"
        title="Benefits"
        data={benefits}
        isLoading={isLoading}
        paginationMeta={meta}
        onPageChange={setPage}
        emptyMessage="No benefits found"
        renderHeader={() => (
          <>
            <TableHead>Text</TableHead>
            <TableHead className="w-24" />
          </>
        )}
        renderRow={(benefit: Benefit) => (
          <>
            <TableCell className="py-4">{benefit.text}</TableCell>
            <TableCell>
              <RelationActionButton
                type="button"
                onClick={() => handleDeleteClick(benefit.id)}
                className="text-delete"
              >
                Delete
              </RelationActionButton>
            </TableCell>
          </>
        )}
        footerActions={
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={handleAddNew}
          >
            Create new benefit
          </Button>
        }
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete benefit"
        description="Are you sure you want to delete this benefit?"
        onConfirm={handleDeleteConfirm}
        isPending={isDeleting}
      />

      <BenefitDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSubmit={handleSubmit}
        isPending={isCreating}
      />
    </>
  );
};
